import { action, computed, decorate, observable, runInAction } from 'mobx';
import { error, fetchData, RootStore } from './RootStore';

export type searchResultsProps = {
  id: string;
  name: string;
  path: string;
  summary: string;
  metadata: {
    phaseColor: string | null;
    phaseName: string | null;
    phasePath: string | null;
  };
};

type searchProps = {
  query: string;
  resultFound: string;
  totalPages: string;
  currentPage?: string;
  results?: searchResultsProps[];
  noResults?: string;
};

export class SearchStore {
  rootStore: RootStore;
  isPending: boolean;
  error: error;
  searchResults: searchProps | null;
  cacheResults: { [key: string]: searchProps };
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.isPending = true;
    this.searchResults = null;
    this.cacheResults = {};
    this.error = null;
  }

  makeSearch = async (url: string, query: string | null, page: string | null) => {
    if (!query) {
      this.searchResults = null;
      this.isPending = false;
      return;
    }

    const requestUrl = `${process.env.REACT_APP_API || ''}${url}?${process.env
      .REACT_APP_JSON_DESIGN || ''}&${process.env.REACT_APP_BLANK_PAGE || ''}&query=${query}${
      page ? `&current_result_page=${page}` : ''
    }`;

    if (this.cacheResults[requestUrl]) {
      this.searchResults = this.cacheResults[requestUrl];
      return;
    }

    this.isPending = true;
    this.error = null;
    try {
      const searchResults: searchProps = await fetchData(requestUrl);
      runInAction(() => {
        this.isPending = false;
        this.searchResults = searchResults;
        this.cacheResults = { ...this.cacheResults, [requestUrl]: searchResults };
      });
    } catch (error) {
      runInAction(() => {
        this.error = { name: error.name, message: error.message };
        this.isPending = false;
      });
    }
  };

  get getShowingFrom() {
    if (this.searchResults) {
      return Number(this.searchResults.currentPage) === 1
        ? 1
        : (Number(this.searchResults.currentPage) - 1) *
            Number(process.env.REACT_APP_SEARCH_RESULT_PER_PAGE as string) +
            1;
    } else {
      return null;
    }
  }

  get getShowingTo() {
    if (this.searchResults) {
      return Number(this.searchResults.resultFound) <=
        Number(process.env.REACT_APP_SEARCH_RESULT_PER_PAGE as string) ||
        Number(this.searchResults.resultFound) <=
          Number(process.env.REACT_APP_SEARCH_RESULT_PER_PAGE as string) *
            Number(this.searchResults.currentPage)
        ? Number(this.searchResults.resultFound)
        : Number(this.searchResults.currentPage) *
            Number(process.env.REACT_APP_SEARCH_RESULT_PER_PAGE as string);
    } else {
      return null;
    }
  }
}

decorate(SearchStore, {
  error: observable,
  getShowingFrom: computed,
  getShowingTo: computed,
  isPending: observable,
  makeSearch: action,
  searchResults: observable,
});
